import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "@/context/ThemeContext";

export default function FAQ() {
  const { t } = useTranslation("home");
  const { effectiveTheme } = useTheme();
  const isLight = effectiveTheme === "light";
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = useMemo(
    () => [
      {
        question: t("faq.item1.q"),
        answer: t("faq.item1.a"),
      },
      {
        question: t("faq.item2.q"),
        answer: t("faq.item2.a"),
      },
      {
        question: t("faq.item3.q"),
        answer: t("faq.item3.a"),
      },
      {
        question: t("faq.item4.q"),
        answer: t("faq.item4.a"),
      },
      {
        question: t("faq.item5.q"),
        answer: t("faq.item5.a"),
      },
      {
        question: t("faq.item6.q"),
        answer: t("faq.item6.a"),
      },
      {
        question: t("faq.item7.q"),
        answer: t("faq.item7.a"),
      },
      {
        question: t("faq.item8.q"),
        answer: t("faq.item8.a"),
      },
    ],
    [t]
  );

  return (
    <section
      id="faq"
      className={`relative py-24 sm:py-32 ${
        isLight ? "bg-[#f5f5f7]" : "bg-cosmic-950"
      }`}
    >
      {/* Divider */}
      <div
        className={`mx-auto w-2/3 max-w-3xl h-px mb-24 sm:mb-32 bg-gradient-to-r from-transparent to-transparent ${
          isLight ? "via-black/10" : "via-white/10"
        }`}
      />

      <div className="w-full flex flex-col items-center justify-center">
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <span
              className={`inline-block text-5xl md:text-7xl lg:text-8xl font-extrabold tracking-tight select-none whitespace-nowrap ${
                isLight ? "text-[#1c1c1e]/[0.04]" : "text-white/[0.04]"
              }`}
            >
              {t("faq.bg_text")}
            </span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className={`text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mt-4 ${
              isLight ? "text-[#1c1c1e]" : "text-white"
            }`}
          >
            {t("faq.title")}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className={`mt-5 text-base md:text-lg ${
              isLight ? "text-[#6e6e73]" : "text-silver-400"
            }`}
          >
            {t("faq.subtitle")}
          </motion.p>
        </div>

        {/* FAQ List */}
        <div className="w-full max-w-7xl px-6 sm:px-10">
          <div className="flex flex-col gap-2">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className={`border-b ${
                  isLight ? "border-black/10" : "border-white/10"
                }`}
              >
                <button
                  onClick={() =>
                    setOpenIndex(openIndex === index ? null : index)
                  }
                  className={`w-full flex items-center justify-between gap-6 px-4 py-4 sm:px-6 sm:py-5 text-left transition-colors duration-200 rounded-lg cursor-pointer ${
                    isLight
                      ? "hover:bg-black/5"
                      : "hover:bg-white/5"
                  }`}
                >
                  <span
                    className={`text-sm sm:text-base lg:text-lg font-medium ${
                      isLight ? "text-[#1c1c1e]" : "text-white"
                    }`}
                  >
                    {faq.question}
                  </span>
                  <span
                    className={`flex h-7 w-7 items-center justify-center shrink-0 ${
                      isLight ? "text-[#6e6e73]" : "text-silver-400"
                    }`}
                  >
                    <i
                      className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${
                        openIndex === index ? "rotate-180" : ""
                      }`}
                    />
                  </span>
                </button>
                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 pt-2 pb-5 sm:px-6 sm:pb-6">
                        <p
                          className={`text-sm sm:text-base leading-relaxed ${
                            isLight ? "text-[#6e6e73]" : "text-silver-400"
                          }`}
                        >
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}